import { Component, OnInit } from '@angular/core';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';

/**
 *  PARTNEREK – szervezetek és személyek törzse.
 *
 *  Az iratkezelésben a küldő és a címzett nem szabad szöveg: azonosítható partner.
 *  Ez a képernyő nem csak nyilvántart, hanem MEGMONDJA, KINEK NEM LEHET
 *  elektronikusan kézbesíteni, és miért – szervezetnél a BKSZ-postafiók (KRID vagy
 *  rövid név), személynél a természetes azonosítók hiányoznak. Tömeges kiküldés előtt
 *  ez a lista mondja meg, hol kell adatot pótolni; ne a kiküldés végén derüljön ki,
 *  hogy tizenketten kimaradtak.
 */
@Component({
  selector: 'app-ikt-partnerek',
  templateUrl: './partnerek.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktPartnerekComponent implements OnInit {
  nezet: 'szemely' | 'szervezet' = 'szemely';
  szoveg = '';
  csakHianyos = false;
  partnerek: any[] = [];
  betoltes = false;

  szerkesztett: any = null;
  importFut = false;

  readonly KAPU_TIPUSOK = [
    { kulcs: 'nincs', nev: 'nincs' },
    { kulcs: 'ugyfelkapu', nev: 'ügyfélkapu (tárhely)' },
    { kulcs: 'cegkapu', nev: 'cégkapu' },
    { kulcs: 'hivatali_kapu', nev: 'hivatali kapu' },
  ];

  /* ── Kitöltés a hivatali kapus névsorból ──
   *  A KRID és a rövid név elgépelése nem hibaüzenetet ad, hanem kézbesíthetetlen
   *  küldeményt, ezért a hivatalos névsorból választunk. A névsort a kiszolgáló tartja
   *  naprakészen (Beállítások → Hivatali kapu névsor). */
  hivKeres = '';
  hivTalalatok: any[] = [];
  hivUres = false;
  private hivIdozito: any = null;

  hivKeresValt() {
    clearTimeout(this.hivIdozito);
    this.hivIdozito = setTimeout(async () => {
      const k = (this.hivKeres || '').trim();
      if (k.length < 2) { this.hivTalalatok = []; this.hivUres = false; return; }
      try {
        this.hivTalalatok = (await this.ikt.hivatalKereses(k, 12))?.talalatok || [];
        this.hivUres = !this.hivTalalatok.length;
      } catch { this.hivTalalatok = []; this.hivUres = true; }
    }, 300);
  }

  /** A kiválasztott szervezet adatai a szerkesztőbe. A nevet csak akkor írjuk felül,
   *  ha még üres – a könyvelésben megszokott „saját” elnevezést ne törölje el. */
  hivatalValaszt(h: any) {
    if (!this.szerkesztett) return;
    this.szerkesztett.krid = h.krid || this.szerkesztett.krid;
    this.szerkesztett.rovidnev = h.rovidnev || this.szerkesztett.rovidnev;
    if (!String(this.szerkesztett.nev || '').trim()) this.szerkesztett.nev = h.nev;
    if (!String(this.szerkesztett.email || '').trim() && h.email) this.szerkesztett.email = h.email;
    if (!this.szerkesztett.kapu_tipus || this.szerkesztett.kapu_tipus === 'nincs') {
      this.szerkesztett.kapu_tipus = 'hivatali_kapu';
    }
    this.hivTalalatok = [];
    this.hivKeres = '';
    this.data.sendToast(`${h.nev} adatai betöltve (KRID: ${h.krid || '—'}).`, 'success');
  }

  constructor(private ikt: IktatgatoService, private data: DataService) {}

  async ngOnInit() { await this.betolt(); }

  async betolt() {
    this.betoltes = true;
    try {
      this.partnerek = await this.ikt.partnertorzs({ tipus: this.nezet, kereses: this.szoveg.trim() });
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A partnertörzs nem tölthető be.', 'error');
    } finally { this.betoltes = false; }
  }

  get lista() {
    return this.csakHianyos ? this.partnerek.filter((p) => p.kapu_hiany?.length) : this.partnerek;
  }

  get hianyosDb() { return this.partnerek.filter((p) => p.kapu_hiany?.length).length; }

  /* ══════════════════════════════════════════════════════════════════════
   *  DUPLIKÁTUMOK ÖSSZEVONÁSA
   *
   *  A tömeges feladás egy ideig MINDEN címzetthez új partnert hozott létre, mert a
   *  keresés a rossz forrásnévre ment. A duplikátum nem csak rendetlenség: a kapcsolati
   *  kód és a természetes azonosítók a RÉGI soron ülnek, a küldés viszont az újra
   *  hivatkozik – így a kézzel beírt kapcsolati kód „eltűnt", és a hivatali kapus
   *  kiküldés elakadt. A hiba javítva; ez a blokk a már keletkezett párokat takarítja.
   * ═════════════════════════════════════════════════════════════════════ */
  duplikatumok: any[] = [];
  duplFut = false;
  duplValasztas: any = {};

  async duplikatumokBetolt() {
    this.duplFut = true;
    try {
      const r = await this.ikt.partnerDuplikatumok();
      this.duplikatumok = r?.tetelek || [];
      for (const cs of this.duplikatumok) this.duplValasztas[cs.kulcs] = cs.javasolt;
      if (!this.duplikatumok.length) this.data.sendToast('Nem találtunk azonos nevű partnereket.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A duplikátum-kereső nem futott le.', 'error');
    } finally { this.duplFut = false; }
  }

  async egyesit(cs: any) {
    const megtart = this.duplValasztas[cs.kulcs];
    if (!megtart) { this.data.sendToast('Válaszd ki, melyiket tartsuk meg.', 'warning'); return; }
    const beolvaszt = cs.sorok.map((p: any) => p.id).filter((id: string) => id !== megtart);
    this.duplFut = true;
    try {
      const r = await this.ikt.partnerEgyesites(megtart, beolvaszt);
      const db = Object.values(r?.atvitt || {}).reduce((a: number, b: any) => a + Number(b || 0), 0);
      this.data.sendToast(`${cs.nev}: ${r.beolvasztva} sor összevonva, ${db} hivatkozás átvéve`
        + (r.potolt_mezok?.length ? ` (pótolt adat: ${r.potolt_mezok.join(', ')})` : '') + '.', 'success');
      this.duplikatumok = this.duplikatumok.filter((x) => x.kulcs !== cs.kulcs);
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az összevonás nem sikerült.', 'error');
    } finally { this.duplFut = false; }
  }

  async nezetValt(n: 'szemely' | 'szervezet') { this.nezet = n; this.szerkesztett = null; await this.betolt(); }

  uj() {
    this.szerkesztett = {
      uj: true, tipus: this.nezet === 'szervezet' ? 'szervezet' : 'szemely',
      nev: '', kapu_tipus: this.nezet === 'szervezet' ? 'hivatali_kapu' : 'ugyfelkapu',
      krid: '', rovidnev: '', adoszam: '', azonosito: '',
      szuletesi_nev: '', anyja_neve: '', szul_hely: '', szul_ido: '',
      irsz: '', telepules: '', cim: '', email: '', telefon: '', megjegyzes: '',
    };
  }

  szerkeszt(p: any) { this.szerkesztett = { ...p, uj: false }; }
  megse() { this.szerkesztett = null; }

  async ment() {
    const p = this.szerkesztett;
    if (!p?.nev?.trim()) { this.data.sendToast('A név kötelező.', 'warning'); return; }
    try {
      if (p.uj) await this.ikt.partnerFelvesz(p);
      else await this.ikt.partnerModosit(p.id, p);
      this.data.sendToast('Mentve.');
      this.szerkesztett = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  async inaktival(p: any) {
    if (!confirm(`Inaktívvá teszed: ${p.nev}? A meglévő iratok érintetlenek maradnak.`)) return;
    try {
      await this.ikt.partnerModosit(p.id, { inaktiv: true });
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A művelet nem sikerült.', 'error');
    }
  }

  /**
   *  Dolgozók behúzása. A meglévő sorokat csak KIEGÉSZÍTI: ami kézzel be lett írva
   *  (pl. a hiányzó anyja neve), azt nem írja felül üressel.
   */
  async importal(forras: 'allomany' | 'ber_alkalmazott') {
    this.importFut = true;
    try {
      const r = await this.ikt.partnerImport(forras);
      this.data.sendToast(`${r.uj} új, ${r.frissitve} frissítve (összesen ${r.osszes}).`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az import nem sikerült.', 'error');
    } finally { this.importFut = false; }
  }
}
